import loadable from '@loadable/component';

import { useIsIntersecting } from '@/pages/CompanyReport/components/Overview/components/DesktopSidebarWithSnowflake/hooks/useIsIntersecting';
import { HEADER_BOTTOM } from '@/pages/CompanyReport/components/Overview/components/DeprecatedHeader';
import useCopyUrlToClipboard from '@/pages/CompanyReport/hooks/useCopyUrlToClipboard';
import { useCreateNotesSidebar } from '@/pages/CompanyReport/hooks/useCreateNotesSidebar';
import { useCompanyNoteModal } from '@components/Note';
import { Portal } from '@simplywallst/ui-core';
import { Button, cn } from '@simplywallst/ui-core/next';
import { useState, type ComponentProps, type ReactNode } from 'react';

import EditSolidSVG from '@simplywallst/ui-core/icons/EditSolid';
import NotesOutlineSVG from '@simplywallst/ui-core/icons/NotesOutline';
import PlusSolidSVG from '@simplywallst/ui-core/icons/PlusSolid';
import ShareOutlineSVG from '@simplywallst/ui-core/icons/ShareOutline';
import useUser from '@/hooks/useUser';
import { useTracking } from 'react-tracking';

const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle')
);

const MenuOverlay = ({ isHidden }: { isHidden: boolean }) => {
  return (
    <div
      aria-hidden={isHidden ? 'true' : 'false'}
      className="fixed left-0 top-0 z-10 h-lvh w-lvw bg-p-black opacity-70 transition-opacity aria-hidden:size-0 aria-hidden:opacity-0"
    />
  );
};

const Menu = ({
  isHidden,
  children,
}: {
  isHidden: boolean;
  children: ReactNode;
}) => {
  return (
    <div
      aria-hidden={isHidden ? 'true' : 'false'}
      className="fixed bottom-x19 right-[max(calc(50vw-600px-72px),theme(space.x2))] z-20 mb-[env(safe-area-inset-bottom)] flex flex-col gap-x1 overflow-hidden opacity-100 aria-hidden:size-0 aria-hidden:opacity-0"
    >
      {children}
    </div>
  );
};

const MenuButton = ({
  isHidden,
  isMenuOpen,
  onClick,
}: {
  isHidden: boolean;
  isMenuOpen: boolean;
  onClick: ComponentProps<typeof Button>['onClick'];
}) => {
  return (
    <div
      aria-hidden={isHidden && !isMenuOpen ? 'true' : 'false'}
      className="fixed bottom-0 right-[max(calc(50vw-600px-72px),theme(space.x2))] z-20 mb-[env(safe-area-inset-bottom)] size-x7 -translate-y-x10 rounded-full transition-transform aria-hidden:translate-y-x7"
    >
      <Button
        className="size-x7 rounded-full"
        variant={isMenuOpen ? 'subtle' : 'primary'}
        onClick={onClick}
      >
        <PlusSolidSVG
          aria-label="More Actions"
          data-cy-id="more-actions"
          className={cn([
            'rotate-0 !fill-solid !opacity-100 transition-transform',
            isMenuOpen && 'rotate-45',
          ])}
        />
      </Button>
    </div>
  );
};

interface Props {
  companyId?: string;
  tickerSymbol?: string;
}

export const FloatingMenu = ({ companyId, tickerSymbol }: Props) => {
  const { data: user } = useUser();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { openSidebar, isSidebarShowing, shouldShowSidebar } =
    useCreateNotesSidebar({
      companyId,
      uniqueSymbol: tickerSymbol,
    });
  const { openNoteModal, modalState } = useCompanyNoteModal();
  const copyUrlToClipboard = useCopyUrlToClipboard();
  const isIntersecting = useIsIntersecting(HEADER_BOTTOM);
  const { trackEvent } = useTracking({
    subject: 'floatingMenu',
    type: 'track',
    action: 'click',
  });

  if (!companyId || !tickerSymbol) {
    return null;
  }

  const isMenuButtonVisible =
    (isIntersecting !== 'yes' || isMenuOpen) &&
    !isSidebarShowing &&
    modalState === 'idle';

  return (
    <Portal portalId="action-menu">
      <>
        <MenuOverlay isHidden={!isMenuOpen} />

        <Menu isHidden={!isMenuOpen}>
          {RUNTIME_ENV === 'native' && (
            <div className="flex items-center justify-end gap-x1_5">
              <div className="text-sm text-solid">Share</div>
              <Button
                className="size-x7 rounded-full"
                onClick={() => {
                  copyUrlToClipboard();
                  setIsMenuOpen(false);
                  trackEvent({
                    modifier: 'share',
                  });
                }}
              >
                <ShareOutlineSVG aria-label="Share" />
              </Button>
            </div>
          )}
          {user.loggedIn && (
            <div className="flex items-center justify-end gap-x1_5">
              <div className="text-sm text-solid">Add to watchlist</div>
              <div className="flex size-x7 items-center">
                <WatchlistAddRemoveCompanyToggle
                  aria-label="Add to watchlist"
                  className="!h-x7 w-x7"
                  companyId={companyId}
                  tickerSymbol={tickerSymbol}
                  styleType="louderLarge"
                  minimal
                  onClick={() => {
                    setIsMenuOpen(false);
                    trackEvent({
                      modifier: 'addToWatchlist',
                    });
                  }}
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-x1_5">
            <div className="text-sm text-solid">Add Note</div>
            <Button
              className="size-x7 rounded-full"
              variant="primary"
              onClick={() => {
                openNoteModal({
                  companyId,
                  uniqueSymbol: tickerSymbol,
                });
                setIsMenuOpen(false);
                trackEvent({
                  modifier: 'addNote',
                });
              }}
            >
              <EditSolidSVG aria-label="Add Note" />
            </Button>
          </div>
          <div className="flex items-center justify-end gap-x1_5">
            <div className="text-sm text-solid">Notes</div>
            {/* This wrapper div handles the fact that our buttons use opacity in their bg colours for disabled */}
            <div className="size-x7 rounded-full bg-surface-2">
              <Button
                className="size-x7 rounded-full"
                variant="primary"
                isDisabled={!shouldShowSidebar}
                onClick={() => {
                  openSidebar();
                  setIsMenuOpen(false);
                  trackEvent({
                    modifier: 'notes',
                  });
                }}
              >
                <NotesOutlineSVG aria-label="Notes" />
              </Button>
            </div>
          </div>
        </Menu>

        <MenuButton
          isHidden={!isMenuButtonVisible}
          isMenuOpen={isMenuOpen}
          onClick={() => {
            setIsMenuOpen((open) => !open);
            trackEvent({
              modifier: 'menu',
              action: isMenuOpen ? 'close' : 'open',
            });
          }}
        />
      </>
    </Portal>
  );
};
